'use client';

import { MYSTERY_ROOM_STANDARD_AMBIENT_SOUND_DEFINITIONS } from './ambientLibrary';
import {
  MYSTERY_ROOM_ACCESS_CARD_ASSETS,
  MYSTERY_ROOM_FLASHLIGHT_ASSETS,
  MYSTERY_ROOM_GLOCK19_ASSETS,
} from './itemAssets';
import type {
  MysteryRoomAccessCardLevel,
  MysteryRoomAccessLevel,
  MysteryRoomCustomObjectKind,
  MysteryRoomMapDefinition,
  MysteryRoomRect,
  MysteryRoomSegment,
  MysteryRoomSurfaceTexture,
  MysteryRoomTextureLayer,
  MysteryRoomVec2,
} from './mapTypes';

type WallGap = {
  side: 'top' | 'right' | 'bottom' | 'left';
  from: number;
  to: number;
};

type TextureFit = NonNullable<MysteryRoomSurfaceTexture['fit']>;

function point(x: number, y: number): MysteryRoomVec2 {
  return { x, y };
}

function surfaceTexture(
  builtinTextureId: string,
  opacity = 0.94,
  tileSize = 260,
  fit: TextureFit = 'repeat',
): MysteryRoomSurfaceTexture {
  return {
    builtinTextureId,
    fit,
    opacity,
    tileSize,
    effects: {
      contrast: 0.08,
      reflection: 0.08,
      grain: 0.04,
    },
  };
}

function textureLayer(
  id: string,
  builtinTextureId: string,
  opacity = 0.9,
  fit: TextureFit = 'repeat',
  tileSize = 240,
): MysteryRoomTextureLayer {
  return {
    id,
    label: id,
    builtinTextureId,
    fit,
    opacity,
    tileSize,
    visible: true,
    effects: {
      contrast: 0.08,
      shading: 0.08,
      grain: 0.05,
    },
  };
}

const WALL_SURFACE = surfaceTexture('realism_wall_brushed_metal', 0.96, 220);
const DOOR_TEXTURE_ID = 'realism_riveted_steel_plates';

function wall(x1: number, y1: number, x2: number, y2: number, texture = WALL_SURFACE): MysteryRoomSegment {
  return { start: point(x1, y1), end: point(x2, y2), texture };
}

function splitRange(start: number, end: number, gaps: WallGap[]) {
  const ranges: Array<[number, number]> = [];
  let cursor = start;
  const sorted = [...gaps]
    .map((gap) => ({
      from: Math.max(start, Math.min(end, Math.min(gap.from, gap.to))),
      to: Math.max(start, Math.min(end, Math.max(gap.from, gap.to))),
    }))
    .filter((gap) => gap.to > gap.from)
    .sort((a, b) => a.from - b.from);

  for (const gap of sorted) {
    if (gap.from > cursor) ranges.push([cursor, gap.from]);
    cursor = Math.max(cursor, gap.to);
  }
  if (cursor < end) ranges.push([cursor, end]);
  return ranges;
}

function rectWalls(rect: MysteryRoomRect, gaps: WallGap[] = [], texture = WALL_SURFACE): MysteryRoomSegment[] {
  const segments: MysteryRoomSegment[] = [];
  const horizontal = (side: WallGap['side'], y: number) => {
    splitRange(
      rect.x,
      rect.x + rect.width,
      gaps.filter((gap) => gap.side === side),
    ).forEach(([from, to]) => {
      segments.push(wall(from, y, to, y, texture));
    });
  };
  const vertical = (side: WallGap['side'], x: number) => {
    splitRange(
      rect.y,
      rect.y + rect.height,
      gaps.filter((gap) => gap.side === side),
    ).forEach(([from, to]) => {
      segments.push(wall(x, from, x, to, texture));
    });
  };

  horizontal('top', rect.y);
  vertical('right', rect.x + rect.width);
  horizontal('bottom', rect.y + rect.height);
  vertical('left', rect.x);
  return segments;
}

const PROP_TEXTURES: Partial<Record<MysteryRoomMapDefinition['props'][number]['kind'], string>> = {
  airlock_console: 'pixel_wall_terminal_panel',
  locker: 'realism_riveted_steel_plates',
  crate_stack: 'realism_warm_wood_planks',
  lab_bench: 'realism_floor_lab_tile',
  microscope: 'realism_white_marble_veins',
  sample_tank: 'realism_green_glass_blocks',
  wall_terminal: 'pixel_wall_terminal_panel',
  server_rack: 'sci_fi_industrial_sci_fi_panel',
  server_console: 'sci_fi_blue_holographic_grid',
  medical_bed: 'realism_beige_ceramic_tile',
  surgical_lamp: 'realism_soft_white_marble',
  med_monitor: 'sci_fi_cyan_hex_panel',
  reactor_core: 'horror_rusted_metal_plates',
  pipe_run: 'realism_riveted_steel_plates',
  turbine: 'sci_fi_black_gold_tech_grid',
  floor_grate: 'horror_black_ridged_stone',
  warning_stripe: 'sci_fi_road_lane_markings',
};

function prop(
  id: string,
  kind: MysteryRoomMapDefinition['props'][number]['kind'],
  zoneId: string,
  rect: MysteryRoomRect,
  color: number,
  accentColor = 0x22d3ee,
  rotation = 0,
): MysteryRoomMapDefinition['props'][number] {
  const textureId = PROP_TEXTURES[kind];
  return {
    id,
    kind,
    zoneId,
    rect,
    color,
    accentColor,
    rotation,
    textures: textureId ? [textureLayer(`${id}_texture`, textureId, 0.86, 'cover', 180)] : undefined,
  };
}

const CUSTOM_OBJECT_TEXTURES: Partial<Record<MysteryRoomCustomObjectKind, string>> = {
  primitive: 'sci_fi_cyan_hex_panel',
  table: 'realism_warm_wood_planks',
  wall: 'realism_wall_brushed_metal',
  door: DOOR_TEXTURE_ID,
  equipment: 'sci_fi_industrial_sci_fi_panel',
  badge: 'sci_fi_cyan_hex_panel',
  weapon: 'realism_riveted_steel_plates',
  decor: 'sci_fi_green_bioluminescent_ground',
};

function object(
  id: string,
  kind: MysteryRoomCustomObjectKind,
  zoneId: string,
  label: string,
  rect: MysteryRoomRect,
  color: number,
  options: Partial<MysteryRoomMapDefinition['customObjects'][number]> = {},
): MysteryRoomMapDefinition['customObjects'][number] {
  const blocksMovement = options.collision?.kind === 'rect' ? options.collision.blocksMovement : false;
  const blocksSight = options.blocksSight ?? (options.collision?.kind === 'rect' ? options.collision.blocksSight : false);
  const textureId = CUSTOM_OBJECT_TEXTURES[kind];
  return {
    id,
    kind,
    zoneId,
    rect,
    rotation: options.rotation ?? 0,
    color,
    shape: options.shape ?? { kind: 'rect' },
    label,
    collision: options.collision ?? { kind: 'none' },
    collisionEnabled: options.collisionEnabled ?? (blocksMovement || blocksSight),
    physicsEnabled: options.physicsEnabled ?? false,
    weightKg: options.weightKg,
    itemId: options.itemId,
    pickupKind: options.pickupKind,
    accessCardLevel: options.accessCardLevel,
    grantsAccess: options.grantsAccess,
    inventoryIcon: options.inventoryIcon,
    inventoryIconAssetId: options.inventoryIconAssetId,
    inventoryIconAssetUrl: options.inventoryIconAssetUrl,
    inventoryIconAssetName: options.inventoryIconAssetName,
    inventoryIconAssetMimeType: options.inventoryIconAssetMimeType,
    assetUrl: options.assetUrl,
    assetName: options.assetName,
    assetMimeType: options.assetMimeType,
    assetFit: options.assetFit,
    assetTransform: options.assetTransform,
    assetAlphaCollisionEnabled: options.assetAlphaCollisionEnabled,
    textures: options.textures ?? (textureId ? [textureLayer(`${id}_texture`, textureId, 0.84, 'cover', 190)] : undefined),
    blocksSight,
    interactive: options.interactive ?? false,
    tags: options.tags ?? [],
    worldLabelVisibility: options.worldLabelVisibility,
    linkedLightIds: options.linkedLightIds,
    onPickupActions: options.onPickupActions,
    onActivateActions: options.onActivateActions,
  };
}

function blockingObject(
  id: string,
  zoneId: string,
  label: string,
  rect: MysteryRoomRect,
  color: number,
  textureId = 'sci_fi_road_lane_markings',
): MysteryRoomMapDefinition['customObjects'][number] {
  return object(id, 'equipment', zoneId, label, rect, color, {
    collision: { kind: 'rect', rect, blocksMovement: true, blocksSight: true },
    blocksSight: true,
    tags: ['cover', 'infection'],
    textures: [textureLayer(`${id}_texture`, textureId, 0.88, 'cover', 180)],
  });
}

function pickupObject(
  id: string,
  label: string,
  kind: MysteryRoomCustomObjectKind,
  zoneId: string,
  rect: MysteryRoomRect,
  color: number,
  options: Pick<
    MysteryRoomMapDefinition['customObjects'][number],
    'itemId' | 'pickupKind' | 'grantsAccess' | 'accessCardLevel' | 'tags'
  > & {
    visual: {
      worldUrl: string;
      inventoryUrl: string;
      inventoryName: string;
      mimeType: string;
    };
  },
): MysteryRoomMapDefinition['customObjects'][number] {
  return object(id, kind, zoneId, label, rect, color, {
    interactive: true,
    textures: [],
    itemId: options.itemId,
    pickupKind: options.pickupKind,
    grantsAccess: options.grantsAccess,
    accessCardLevel: options.accessCardLevel,
    tags: options.tags,
    assetUrl: options.visual.worldUrl,
    assetName: `${options.visual.inventoryName} world`,
    assetMimeType: options.visual.mimeType,
    assetFit: 'contain',
    assetTransform: { offsetX: 0, offsetY: 0, scaleX: 1, scaleY: 1, rotation: 0, clipToBounds: true },
    assetAlphaCollisionEnabled: true,
    inventoryIconAssetUrl: options.visual.inventoryUrl,
    inventoryIconAssetName: options.visual.inventoryName,
    inventoryIconAssetMimeType: options.visual.mimeType,
    worldLabelVisibility: 'near_interactive',
  });
}

function accessCardObject(
  id: string,
  label: string,
  zoneId: string,
  rect: MysteryRoomRect,
  cardLevel: MysteryRoomAccessCardLevel,
  grantsAccess: MysteryRoomAccessLevel,
): MysteryRoomMapDefinition['customObjects'][number] {
  return pickupObject(id, label, 'badge', zoneId, rect, 0xfacc15, {
    itemId: id,
    pickupKind: 'access_card',
    accessCardLevel: cardLevel,
    grantsAccess,
    tags: ['access_card', cardLevel.toLowerCase()],
    visual: MYSTERY_ROOM_ACCESS_CARD_ASSETS[cardLevel],
  });
}

function door(
  id: string,
  label: string,
  fromZoneId: string,
  toZoneId: string,
  requiredAccess: MysteryRoomAccessLevel,
  panel: MysteryRoomRect,
  trigger: MysteryRoomRect,
  orientation: MysteryRoomMapDefinition['doors'][number]['orientation'],
  accessCardObjectIds: string[] = [],
): MysteryRoomMapDefinition['doors'][number] {
  return {
    id,
    label,
    fromZoneId,
    toZoneId,
    requiredAccess,
    accessMode: accessCardObjectIds.length ? 'card_required' : 'public',
    accessCardObjectIds,
    panel,
    trigger,
    orientation,
    textures: [textureLayer(`${id}_texture`, DOOR_TEXTURE_ID, 0.9, 'cover', 190)],
  };
}

function navEdge(
  id: string,
  fromNodeId: string,
  toNodeId: string,
  distanceCost: number,
  doorId?: string,
  requiredAccess?: MysteryRoomAccessLevel,
): MysteryRoomMapDefinition['navEdges'][number] {
  return { id, fromNodeId, toNodeId, distanceCost, doorId, requiredAccess };
}

function twoWayEdge(
  id: string,
  fromNodeId: string,
  toNodeId: string,
  distanceCost: number,
  doorId?: string,
  requiredAccess?: MysteryRoomAccessLevel,
): MysteryRoomMapDefinition['navEdges'] {
  return [
    navEdge(id, fromNodeId, toNodeId, distanceCost, doorId, requiredAccess),
    navEdge(`${id}_back`, toNodeId, fromNodeId, distanceCost, doorId, requiredAccess),
  ];
}

const AIRLOCK = { x: 420, y: 300, width: 640, height: 520 };
const MAIN_CORRIDOR = { x: 780, y: 780, width: 2600, height: 310 };
const HUB = { x: 1840, y: 1080, width: 900, height: 660 };
const WEST_SERVICE = { x: 880, y: 1040, width: 260, height: 760 };
const EAST_SERVICE = { x: 3320, y: 1040, width: 260, height: 1100 };
const SOUTH_LINK = { x: 2100, y: 1720, width: 280, height: 220 };
const RESEARCH = { x: 1450, y: 260, width: 900, height: 520 };
const SERVER = { x: 2660, y: 260, width: 620, height: 520 };
const MED_BAY = { x: 3350, y: 360, width: 700, height: 520 };
const REAGENT = { x: 3330, y: 1180, width: 820, height: 620 };
const SECURITY = { x: 350, y: 980, width: 760, height: 520 };
const ENGINEERING = { x: 680, y: 1760, width: 900, height: 560 };
const ARCHIVE = { x: 1760, y: 1880, width: 900, height: 440 };
const ARCHIVE_TUNNEL_LINK = { x: 2220, y: 2300, width: 260, height: 130 };
const TUNNELS = { x: 1620, y: 2380, width: 1780, height: 340 };
const TUNNEL_EAST_LINK = { x: 3340, y: 2380, width: 190, height: 220 };
const EVAC = { x: 3480, y: 1980, width: 840, height: 620 };

const WALLS: MysteryRoomSegment[] = [
  ...rectWalls(AIRLOCK, [
    { side: 'right', from: 740, to: 840 },
    { side: 'bottom', from: 780, to: 1060 },
  ]),
  ...rectWalls(HUB, [
    { side: 'top', from: 1980, to: 2580 },
    { side: 'left', from: 1230, to: 1530 },
    { side: 'right', from: 1230, to: 1530 },
    { side: 'bottom', from: 2100, to: 2380 },
  ]),
  ...rectWalls(RESEARCH, [{ side: 'bottom', from: 1710, to: 2110 }]),
  ...rectWalls(SERVER, [{ side: 'bottom', from: 2810, to: 3100 }]),
  ...rectWalls(MED_BAY, [
    { side: 'left', from: 740, to: 900 },
    { side: 'bottom', from: 3350, to: 3420 },
  ]),
  ...rectWalls(REAGENT, [{ side: 'left', from: 1330, to: 1640 }], surfaceTexture('horror_rusted_metal_plates', 0.96, 220)),
  ...rectWalls(SECURITY, [
    { side: 'right', from: 1030, to: 1240 },
    { side: 'bottom', from: 870, to: 1080 },
  ]),
  ...rectWalls(ENGINEERING, [{ side: 'top', from: 900, to: 1120 }]),
  ...rectWalls(ARCHIVE, [
    { side: 'top', from: 2100, to: 2380 },
    { side: 'bottom', from: 2220, to: 2480 },
  ]),
  ...rectWalls(TUNNELS, [
    { side: 'top', from: 2220, to: 2480 },
    { side: 'right', from: 2380, to: 2600 },
  ], surfaceTexture('horror_dark_stone_bricks', 0.95, 220)),
  ...rectWalls(EVAC, [
    { side: 'left', from: 1980, to: 2140 },
    { side: 'left', from: 2380, to: 2600 },
  ]),
  wall(MAIN_CORRIDOR.x, MAIN_CORRIDOR.y, 1010, MAIN_CORRIDOR.y),
  wall(1100, MAIN_CORRIDOR.y, MAIN_CORRIDOR.x + 620, MAIN_CORRIDOR.y),
  wall(2360, MAIN_CORRIDOR.y, 2640, MAIN_CORRIDOR.y),
  wall(3290, MAIN_CORRIDOR.y, 3330, MAIN_CORRIDOR.y),
  wall(MAIN_CORRIDOR.x, MAIN_CORRIDOR.y + MAIN_CORRIDOR.height, 1060, MAIN_CORRIDOR.y + MAIN_CORRIDOR.height),
  wall(1160, MAIN_CORRIDOR.y + MAIN_CORRIDOR.height, 1840, MAIN_CORRIDOR.y + MAIN_CORRIDOR.height),
  wall(2740, MAIN_CORRIDOR.y + MAIN_CORRIDOR.height, MAIN_CORRIDOR.x + MAIN_CORRIDOR.width, MAIN_CORRIDOR.y + MAIN_CORRIDOR.height),
  wall(WEST_SERVICE.x, WEST_SERVICE.y, WEST_SERVICE.x, WEST_SERVICE.y + WEST_SERVICE.height),
  wall(WEST_SERVICE.x + WEST_SERVICE.width, 1240, WEST_SERVICE.x + WEST_SERVICE.width, WEST_SERVICE.y + WEST_SERVICE.height),
  wall(EAST_SERVICE.x, EAST_SERVICE.y, EAST_SERVICE.x, 1320),
  wall(EAST_SERVICE.x, 1640, EAST_SERVICE.x, EAST_SERVICE.y + EAST_SERVICE.height),
  wall(EAST_SERVICE.x + EAST_SERVICE.width, EAST_SERVICE.y, EAST_SERVICE.x + EAST_SERVICE.width, EAST_SERVICE.y + EAST_SERVICE.height),
  wall(SOUTH_LINK.x, SOUTH_LINK.y, SOUTH_LINK.x, SOUTH_LINK.y + SOUTH_LINK.height),
  wall(SOUTH_LINK.x + SOUTH_LINK.width, SOUTH_LINK.y, SOUTH_LINK.x + SOUTH_LINK.width, SOUTH_LINK.y + SOUTH_LINK.height),
  wall(ARCHIVE_TUNNEL_LINK.x, ARCHIVE_TUNNEL_LINK.y, ARCHIVE_TUNNEL_LINK.x, ARCHIVE_TUNNEL_LINK.y + ARCHIVE_TUNNEL_LINK.height),
  wall(ARCHIVE_TUNNEL_LINK.x + ARCHIVE_TUNNEL_LINK.width, ARCHIVE_TUNNEL_LINK.y, ARCHIVE_TUNNEL_LINK.x + ARCHIVE_TUNNEL_LINK.width, ARCHIVE_TUNNEL_LINK.y + ARCHIVE_TUNNEL_LINK.height),
  wall(TUNNEL_EAST_LINK.x, TUNNEL_EAST_LINK.y, TUNNEL_EAST_LINK.x, TUNNEL_EAST_LINK.y + TUNNEL_EAST_LINK.height, surfaceTexture('horror_dark_stone_bricks', 0.95, 220)),
];

const LIGHTS: MysteryRoomMapDefinition['lights'] = [
  { id: 'light_airlock_clean', label: 'Шлюзовый свет', zoneId: 'airlock_entry', position: point(720, 560), radius: 520, color: 0xdbeafe, intensity: 0.86 },
  { id: 'light_main_corridor', label: 'Главный коридор', zoneId: 'central_cross', position: point(1880, 930), radius: 760, color: 0xbae6fd, intensity: 0.64, flickerMode: 'random_bulb' },
  { id: 'light_central_hub', label: 'Безопасная зона', zoneId: 'central_cross', position: point(2290, 1400), radius: 820, color: 0x99f6e4, intensity: 0.72 },
  { id: 'light_research_red', label: 'Лампы лаборатории', zoneId: 'research_lab', position: point(1900, 520), radius: 560, color: 0xfca5a5, intensity: 0.74, flickerMode: 'event_flicker' },
  { id: 'light_server_blue', label: 'Свет серверной', zoneId: 'server_room', position: point(2970, 520), radius: 520, color: 0x93c5fd, intensity: 0.78 },
  { id: 'light_medbay', label: 'Медицинский свет', zoneId: 'med_bay', position: point(3710, 640), radius: 540, color: 0xbbf7d0, intensity: 0.74 },
  { id: 'light_reagent_red', label: 'Тревога склада реагентов', zoneId: 'reactor_room', position: point(3760, 1510), radius: 700, color: 0xf87171, intensity: 0.72, flickerMode: 'random_bulb' },
  { id: 'light_security', label: 'Мониторы охраны', zoneId: 'security_room', position: point(720, 1240), radius: 500, color: 0x93c5fd, intensity: 0.62 },
  { id: 'light_engineering', label: 'Инженерные лампы', zoneId: 'engineering_sector', position: point(1120, 2040), radius: 620, color: 0xfbbf24, intensity: 0.7 },
  { id: 'light_archive', label: 'Архивный свет', zoneId: 'archive_room', position: point(2200, 2100), radius: 520, color: 0xc4b5fd, intensity: 0.58 },
  { id: 'light_tunnels', label: 'Красная линия тоннелей', zoneId: 'technical_tunnels', position: point(2500, 2550), radius: 780, color: 0xfb7185, intensity: 0.54, flickerMode: 'random_bulb' },
  { id: 'light_evac', label: 'Маяки эвакуации', zoneId: 'evacuation_zone', position: point(3900, 2290), radius: 700, color: 0xfacc15, intensity: 0.78 },
];

const INTERACTABLES: MysteryRoomMapDefinition['interactables'] = [
  {
    id: 'switch_quarantine_lights',
    kind: 'light_switch',
    zoneId: 'airlock_entry',
    anchor: point(980, 700),
    interactionRadius: 88,
    label: 'Пульт аварийного света',
    worldLabelVisibility: 'near_interactive',
    linkedLightIds: ['light_main_corridor', 'light_central_hub'],
    lightReaction: 'toggle',
    defaultEnabled: true,
    textures: [textureLayer('switch_quarantine_lights_texture', 'pixel_wall_terminal_panel', 0.86, 'cover', 160)],
  },
  {
    id: 'research_sample_case',
    kind: 'inspect',
    zoneId: 'research_lab',
    anchor: point(1850, 560),
    interactionRadius: 94,
    label: 'Контейнер контрольного образца',
    worldLabelVisibility: 'near_interactive',
    textures: [textureLayer('research_sample_case_texture', 'realism_green_glass_blocks', 0.82, 'cover', 160)],
  },
  {
    id: 'server_audit_terminal',
    kind: 'inspect',
    zoneId: 'server_room',
    anchor: point(3000, 650),
    interactionRadius: 96,
    label: 'Терминал аудита шлюзов',
    worldLabelVisibility: 'near_interactive',
    textures: [textureLayer('server_audit_terminal_texture', 'sci_fi_blue_holographic_grid', 0.84, 'cover', 160)],
  },
  {
    id: 'reactor_console',
    kind: 'inspect',
    zoneId: 'reactor_room',
    anchor: point(3670, 1370),
    interactionRadius: 104,
    label: 'Консоль склада реагентов',
    worldLabelVisibility: 'near_interactive',
    textures: [textureLayer('reactor_console_texture', 'pixel_wall_terminal_panel', 0.86, 'cover', 160)],
  },
  {
    id: 'infection_wave_terminal',
    kind: 'inspect',
    zoneId: 'central_cross',
    anchor: point(2290, 1230),
    interactionRadius: 96,
    label: 'Терминал волн заражения',
    worldLabelVisibility: 'near_interactive',
    textures: [textureLayer('infection_wave_terminal_texture', 'sci_fi_cyan_hex_panel', 0.86, 'cover', 170)],
  },
  {
    id: 'mission_evac_beacon',
    kind: 'inspect',
    zoneId: 'evacuation_zone',
    anchor: point(3930, 2260),
    interactionRadius: 110,
    label: 'Маяк эвакуации',
    worldLabelVisibility: 'near_interactive',
    textures: [textureLayer('mission_evac_beacon_texture', 'sci_fi_black_gold_tech_grid', 0.86, 'cover', 170)],
  },
  {
    id: 'engineering_generator',
    kind: 'inspect',
    zoneId: 'engineering_sector',
    anchor: point(1020, 2060),
    interactionRadius: 104,
    label: 'Резервный генератор',
    worldLabelVisibility: 'near_interactive',
    textures: [textureLayer('engineering_generator_texture', 'sci_fi_black_gold_tech_grid', 0.84, 'cover', 170)],
  },
];

const CUSTOM_OBJECTS: MysteryRoomMapDefinition['customObjects'] = [
  object('safe_hub_bio_tree', 'decor', 'central_cross', 'Био-ядро хаба', { x: 2185, y: 1340, width: 210, height: 190 }, 0x22c55e, {
    interactive: true,
    worldLabelVisibility: 'near_interactive',
    tags: ['safe_zone', 'hub'],
    textures: [textureLayer('safe_hub_bio_tree_texture', 'sci_fi_green_bioluminescent_ground', 0.88, 'cover', 180)],
  }),
  object('infection_nest_core', 'equipment', 'research_lab', 'Заражённый узел образцов', { x: 2070, y: 420, width: 160, height: 128 }, 0x7f1d1d, {
    interactive: true,
    worldLabelVisibility: 'near_interactive',
    tags: ['infection', 'nest'],
    textures: [textureLayer('infection_nest_core_texture', 'sci_fi_green_bioluminescent_ground', 0.82, 'cover', 180)],
    onActivateActions: [
      { kind: 'set_alarm', enabled: true },
      { kind: 'set_light_flicker', lightIds: ['light_research_red', 'light_main_corridor'], enabled: true, durationMs: 4500 },
      { kind: 'spawn_smoke', position: point(2110, 480), radius: 260 },
    ],
  }),
  blockingObject('barricade_main_west', 'central_cross', 'Баррикада главного пути', { x: 1220, y: 840, width: 86, height: 190 }, 0x7c2d12),
  blockingObject('barricade_hub_south', 'central_cross', 'Баррикада хаба', { x: 2070, y: 1710, width: 300, height: 46 }, 0x7c2d12),
  blockingObject('reagent_crate_block', 'reactor_room', 'Ящик реагентов', { x: 3900, y: 1590, width: 110, height: 148 }, 0x78350f, 'horror_rusted_metal_plates'),
  object('server_spore_tank_a', 'equipment', 'server_room', 'Заражённый серверный бак', { x: 2735, y: 360, width: 110, height: 250 }, 0x365314, {
    collision: { kind: 'rect', rect: { x: 2735, y: 360, width: 110, height: 250 }, blocksMovement: true, blocksSight: true },
    blocksSight: true,
    tags: ['infection', 'tank'],
    textures: [textureLayer('server_spore_tank_a_texture', 'sci_fi_green_bioluminescent_ground', 0.78, 'cover', 170)],
  }),
  object('repair_station_engineering', 'equipment', 'engineering_sector', 'Ремонтная станция', { x: 1330, y: 2050, width: 150, height: 110 }, 0x0f766e, {
    interactive: true,
    worldLabelVisibility: 'near_interactive',
    tags: ['repair', 'generator'],
    textures: [textureLayer('repair_station_engineering_texture', 'sci_fi_cyan_hex_panel', 0.84, 'cover', 170)],
  }),
  object('security_camera_wall', 'equipment', 'security_room', 'Пульт камер', { x: 475, y: 1080, width: 160, height: 100 }, 0x1d4ed8, {
    interactive: true,
    worldLabelVisibility: 'near_interactive',
    tags: ['camera', 'security'],
    textures: [textureLayer('security_camera_wall_texture', 'pixel_wall_terminal_panel', 0.86, 'cover', 160)],
  }),
  object('vent_tunnel_entrance', 'equipment', 'technical_tunnels', 'Вентиляционная шахта', { x: 1700, y: 2470, width: 170, height: 94 }, 0x334155, {
    tags: ['vent', 'alternate_route'],
    textures: [textureLayer('vent_tunnel_entrance_texture', 'horror_black_ridged_stone', 0.9, 'cover', 160)],
  }),
  object('evac_lift_platform', 'equipment', 'evacuation_zone', 'Эвакуационный подъёмник', { x: 3730, y: 2140, width: 340, height: 280 }, 0x92400e, {
    interactive: true,
    worldLabelVisibility: 'near_interactive',
    tags: ['evac', 'lift'],
    textures: [textureLayer('evac_lift_platform_texture', 'sci_fi_black_gold_tech_grid', 0.88, 'cover', 210)],
  }),
  pickupObject('pickup_pistol_airlock', 'Glock 19', 'weapon', 'airlock_entry', { x: 560, y: 690, width: 74, height: 56 }, 0x64748b, {
    itemId: 'pistol',
    pickupKind: 'weapon',
    tags: ['pistol'],
    visual: MYSTERY_ROOM_GLOCK19_ASSETS,
  }),
  pickupObject('pickup_flashlight_airlock', 'Фонарик', 'equipment', 'airlock_entry', { x: 680, y: 690, width: 58, height: 58 }, 0xfacc15, {
    itemId: 'flashlight',
    pickupKind: 'tool',
    tags: ['flashlight'],
    visual: MYSTERY_ROOM_FLASHLIGHT_ASSETS,
  }),
  accessCardObject('card_lab_l2', 'Пропуск L2: лаборатория', 'airlock_entry', { x: 810, y: 690, width: 62, height: 42 }, 'L2', 'lab'),
  accessCardObject('card_server_l3', 'Пропуск L3: серверная', 'research_lab', { x: 1550, y: 640, width: 62, height: 42 }, 'L3', 'server'),
  accessCardObject('card_reactor_l4', 'Пропуск L4: эвакуация', 'server_room', { x: 3150, y: 650, width: 62, height: 42 }, 'L4', 'reactor'),
];

const EDITOR_WORLD_WIDTH = 12_000;
const EDITOR_WORLD_HEIGHT = 8_000;
const EDITOR_WORLD_MARGIN = 140;
const SOURCE_CONTENT_BOUNDS = { minX: 350, minY: 260, maxX: 4320, maxY: 2720 };

function mapScaleX() {
  return (EDITOR_WORLD_WIDTH - EDITOR_WORLD_MARGIN * 2) / (SOURCE_CONTENT_BOUNDS.maxX - SOURCE_CONTENT_BOUNDS.minX);
}

function mapScaleY() {
  return (EDITOR_WORLD_HEIGHT - EDITOR_WORLD_MARGIN * 2) / (SOURCE_CONTENT_BOUNDS.maxY - SOURCE_CONTENT_BOUNDS.minY);
}

function mapScaleAverage() {
  return (mapScaleX() + mapScaleY()) / 2;
}

function expandPointToEditorBounds(value: MysteryRoomVec2): MysteryRoomVec2 {
  return {
    x: Math.round((EDITOR_WORLD_MARGIN + (value.x - SOURCE_CONTENT_BOUNDS.minX) * mapScaleX()) * 1000) / 1000,
    y: Math.round((EDITOR_WORLD_MARGIN + (value.y - SOURCE_CONTENT_BOUNDS.minY) * mapScaleY()) * 1000) / 1000,
  };
}

function expandSize(value: number, axis: 'x' | 'y' | 'avg') {
  const scale = axis === 'x' ? mapScaleX() : axis === 'y' ? mapScaleY() : mapScaleAverage();
  return Math.round(value * scale * 1000) / 1000;
}

function expandRectToEditorBounds(rect: MysteryRoomRect): MysteryRoomRect {
  const origin = expandPointToEditorBounds(rect);
  return {
    x: origin.x,
    y: origin.y,
    width: expandSize(rect.width, 'x'),
    height: expandSize(rect.height, 'y'),
  };
}

function expandSegmentToEditorBounds(segment: MysteryRoomSegment): MysteryRoomSegment {
  return {
    ...segment,
    start: expandPointToEditorBounds(segment.start),
    end: expandPointToEditorBounds(segment.end),
  };
}

function expandMapToEditorBounds(map: MysteryRoomMapDefinition): MysteryRoomMapDefinition {
  return {
    ...map,
    width: EDITOR_WORLD_WIDTH,
    height: EDITOR_WORLD_HEIGHT,
    spawn: expandPointToEditorBounds(map.spawn),
    spawnSlots: map.spawnSlots.map((slot) => ({
      ...slot,
      x: expandSize(slot.x - SOURCE_CONTENT_BOUNDS.minX, 'x') + EDITOR_WORLD_MARGIN,
      y: expandSize(slot.y - SOURCE_CONTENT_BOUNDS.minY, 'y') + EDITOR_WORLD_MARGIN,
    })),
    visibilityRadius: expandSize(map.visibilityRadius, 'avg'),
    rooms: map.rooms.map((room) => ({
      ...room,
      floor: room.floor.map(expandRectToEditorBounds),
      labelPoint: expandPointToEditorBounds(room.labelPoint),
    })),
    doors: map.doors.map((doorEntry) => ({
      ...doorEntry,
      panel: expandRectToEditorBounds(doorEntry.panel),
      trigger: expandRectToEditorBounds(doorEntry.trigger),
    })),
    walls: map.walls.map(expandSegmentToEditorBounds),
    occluders: map.occluders.map((occluder) => ({
      ...occluder,
      rect: expandRectToEditorBounds(occluder.rect),
    })),
    interactables: map.interactables.map((entry) => ({
      ...entry,
      anchor: expandPointToEditorBounds(entry.anchor),
      interactionRadius: expandSize(entry.interactionRadius, 'avg'),
    })),
    workAnchors: map.workAnchors.map((anchor) => ({
      ...anchor,
      position: expandPointToEditorBounds(anchor.position),
      taskAreaRadius: typeof anchor.taskAreaRadius === 'number' ? expandSize(anchor.taskAreaRadius, 'avg') : anchor.taskAreaRadius,
    })),
    navNodes: map.navNodes.map((node) => ({
      ...node,
      position: expandPointToEditorBounds(node.position),
    })),
    navEdges: map.navEdges.map((edge) => ({
      ...edge,
      distanceCost: expandSize(edge.distanceCost, 'avg'),
    })),
    incidentAnchors: map.incidentAnchors.map((anchor) => ({
      ...anchor,
      position: expandPointToEditorBounds(anchor.position),
    })),
    patrolRoutes: map.patrolRoutes.map((route) => ({
      ...route,
      nodes: route.nodes.map((node) => ({
        ...node,
        x: expandSize(node.x - SOURCE_CONTENT_BOUNDS.minX, 'x') + EDITOR_WORLD_MARGIN,
        y: expandSize(node.y - SOURCE_CONTENT_BOUNDS.minY, 'y') + EDITOR_WORLD_MARGIN,
      })),
    })),
    npcs: map.npcs.map((npc) => ({
      ...npc,
      spawn: expandPointToEditorBounds(npc.spawn),
    })),
    lights: map.lights.map((light) => ({
      ...light,
      position: expandPointToEditorBounds(light.position),
      radius: expandSize(light.radius, 'avg'),
    })),
    hazards: map.hazards.map((hazard) => ({
      ...hazard,
      position: expandPointToEditorBounds(hazard.position),
      radius: expandSize(hazard.radius, 'avg'),
    })),
    props: map.props.map((entry) => ({
      ...entry,
      rect: expandRectToEditorBounds(entry.rect),
    })),
    customObjects: map.customObjects.map((entry) => {
      const collision =
        entry.collision.kind === 'rect'
          ? { ...entry.collision, rect: expandRectToEditorBounds(entry.collision.rect) }
          : entry.collision.kind === 'circle'
            ? {
                ...entry.collision,
                center: expandPointToEditorBounds(entry.collision.center),
                radius: expandSize(entry.collision.radius, 'avg'),
              }
            : entry.collision.kind === 'polygon'
              ? { ...entry.collision, points: entry.collision.points.map(expandPointToEditorBounds) }
              : entry.collision;

      return {
        ...entry,
        rect: expandRectToEditorBounds(entry.rect),
        collision,
      };
    }),
    triggers: map.triggers.map((trigger) => ({
      ...trigger,
      rect: expandRectToEditorBounds(trigger.rect),
      actions: trigger.actions.map((action) => {
        if ((action.kind === 'spawn_smoke' || action.kind === 'explosion') && action.position) {
          return {
            ...action,
            position: expandPointToEditorBounds(action.position),
            radius: typeof action.radius === 'number' ? expandSize(action.radius, 'avg') : action.radius,
          };
        }
        return action;
      }),
    })),
    soundSources: map.soundSources.map((source) => ({
      ...source,
      position: expandPointToEditorBounds(source.position),
      radius: expandSize(source.radius, 'avg'),
    })),
  };
}

const LISAD_9_INFECTION_BASE_MAP: MysteryRoomMapDefinition = {
  id: 'lisad_9_infection_protocol',
  title: 'LISAD-9: Карантинный разлом',
  playableGameModes: ['infection'],
  width: 4600,
  height: 3000,
  spawn: point(720, 620),
  spawnZoneId: 'airlock_entry',
  spawnSlots: [
    { x: 610, y: 560, room: 'airlock_entry', zoneId: 'airlock_entry', label: 'Оператор 1', actor: 'player', side: 'A' },
    { x: 690, y: 650, room: 'airlock_entry', zoneId: 'airlock_entry', label: 'Оператор 2', actor: 'player', side: 'A' },
    { x: 780, y: 560, room: 'airlock_entry', zoneId: 'airlock_entry', label: 'Оператор 3', actor: 'player', side: 'A' },
    { x: 870, y: 650, room: 'airlock_entry', zoneId: 'airlock_entry', label: 'Оператор 4', actor: 'player', side: 'A' },
    { x: 2110, y: 500, room: 'research_lab', zoneId: 'research_lab', label: 'Разлом 1', actor: 'bot', side: 'B' },
    { x: 3900, y: 1580, room: 'reactor_room', zoneId: 'reactor_room', label: 'Разлом 2', actor: 'bot', side: 'B' },
    { x: 2540, y: 2560, room: 'technical_tunnels', zoneId: 'technical_tunnels', label: 'Разлом 3', actor: 'bot', side: 'B' },
    { x: 3850, y: 620, room: 'med_bay', zoneId: 'med_bay', label: 'Разлом 4', actor: 'bot', side: 'B' },
  ],
  visibilityRadius: 860,
  rooms: [
    {
      id: 'airlock_entry',
      title: 'Приёмный шлюз',
      accessLevel: 'public',
      floor: [AIRLOCK],
      floorTexture: surfaceTexture('sci_fi_cyan_hex_panel', 0.92, 240),
      color: 0x164e63,
      labelPoint: point(720, 390),
    },
    {
      id: 'central_cross',
      title: 'Главный коридор / безопасная зона',
      accessLevel: 'public',
      floor: [MAIN_CORRIDOR, HUB, WEST_SERVICE, EAST_SERVICE, SOUTH_LINK],
      floorTexture: surfaceTexture('sci_fi_dark_cyan_circuit_floor', 0.9, 260),
      color: 0x0f766e,
      labelPoint: point(2290, 1200),
    },
    {
      id: 'research_lab',
      title: 'Лаборатория образцов',
      accessLevel: 'lab',
      floor: [RESEARCH],
      floorTexture: surfaceTexture('horror_grimy_glass_tile', 0.9, 220),
      color: 0x991b1b,
      labelPoint: point(1900, 320),
    },
    {
      id: 'server_room',
      title: 'Серверная',
      accessLevel: 'server',
      floor: [SERVER],
      floorTexture: surfaceTexture('sci_fi_blue_holographic_grid', 0.88, 220),
      color: 0x1d4ed8,
      labelPoint: point(2970, 320),
    },
    {
      id: 'med_bay',
      title: 'Медицинский блок',
      accessLevel: 'lab',
      floor: [MED_BAY],
      floorTexture: surfaceTexture('realism_beige_ceramic_tile', 0.9, 220),
      color: 0x166534,
      labelPoint: point(3710, 430),
    },
    {
      id: 'reactor_room',
      title: 'Склад реагентов',
      accessLevel: 'reactor',
      floor: [REAGENT],
      floorTexture: surfaceTexture('horror_worn_cracked_concrete', 0.9, 230),
      color: 0x991b1b,
      labelPoint: point(3740, 1240),
    },
    {
      id: 'security_room',
      title: 'Комната охраны',
      accessLevel: 'public',
      floor: [SECURITY],
      floorTexture: surfaceTexture('sci_fi_industrial_sci_fi_panel', 0.88, 220),
      color: 0x1e3a8a,
      labelPoint: point(730, 1040),
    },
    {
      id: 'engineering_sector',
      title: 'Инженерный сектор',
      accessLevel: 'public',
      floor: [ENGINEERING],
      floorTexture: surfaceTexture('realism_steel_floor_panels', 0.9, 230),
      color: 0x92400e,
      labelPoint: point(1120, 1825),
    },
    {
      id: 'archive_room',
      title: 'Архив',
      accessLevel: 'server',
      floor: [ARCHIVE],
      floorTexture: surfaceTexture('decor_embossed_dark_tile', 0.88, 220),
      color: 0x312e81,
      labelPoint: point(2200, 1945),
    },
    {
      id: 'technical_tunnels',
      title: 'Технические тоннели',
      accessLevel: 'public',
      floor: [ARCHIVE_TUNNEL_LINK, TUNNELS, TUNNEL_EAST_LINK],
      floorTexture: surfaceTexture('horror_cracked_black_floor', 0.9, 240),
      color: 0x7f1d1d,
      labelPoint: point(2500, 2450),
    },
    {
      id: 'evacuation_zone',
      title: 'Зона эвакуации',
      accessLevel: 'reactor',
      floor: [EVAC],
      floorTexture: surfaceTexture('sci_fi_black_gold_tech_grid', 0.9, 240),
      color: 0x854d0e,
      labelPoint: point(3900, 2055),
    },
  ],
  doors: [
    door('door_airlock_blast', 'Шлюзовая переборка', 'airlock_entry', 'central_cross', 'public', { x: 1025, y: 758, width: 74, height: 120 }, { x: 940, y: 710, width: 230, height: 230 }, 'vertical'),
    door('door_research_quarantine', 'Дверь лаборатории', 'central_cross', 'research_lab', 'lab', { x: 1710, y: 748, width: 400, height: 66 }, { x: 1640, y: 700, width: 540, height: 210 }, 'horizontal', ['card_lab_l2']),
    door('door_server_lockdown', 'Замок серверной', 'central_cross', 'server_room', 'server', { x: 2810, y: 748, width: 290, height: 66 }, { x: 2760, y: 690, width: 400, height: 230 }, 'horizontal', ['card_server_l3']),
    door('door_medbay_slide', 'Дверь медблока', 'central_cross', 'med_bay', 'public', { x: 3338, y: 760, width: 74, height: 122 }, { x: 3270, y: 700, width: 230, height: 240 }, 'vertical'),
    door('door_reagent_breach', 'Гермодверь склада', 'central_cross', 'reactor_room', 'reactor', { x: 3304, y: 1330, width: 76, height: 310 }, { x: 3240, y: 1260, width: 230, height: 460 }, 'vertical', ['card_reactor_l4']),
    door('door_security_checkpoint', 'Пост охраны', 'central_cross', 'security_room', 'public', { x: 1075, y: 1030, width: 74, height: 210 }, { x: 1010, y: 960, width: 230, height: 330 }, 'vertical'),
    door('door_engineering_shutter', 'Шлюз инженерного сектора', 'central_cross', 'engineering_sector', 'public', { x: 900, y: 1740, width: 220, height: 66 }, { x: 820, y: 1660, width: 370, height: 230 }, 'horizontal'),
    door('door_archive_lock', 'Дверь архива', 'central_cross', 'archive_room', 'server', { x: 2100, y: 1848, width: 280, height: 66 }, { x: 2020, y: 1780, width: 440, height: 230 }, 'horizontal', ['card_server_l3']),
    door('door_evac_lift', 'Лифт эвакуации', 'technical_tunnels', 'evacuation_zone', 'reactor', { x: 3450, y: 2380, width: 76, height: 220 }, { x: 3370, y: 2290, width: 260, height: 380 }, 'vertical', ['card_reactor_l4']),
  ],
  walls: WALLS,
  occluders: [
    { id: 'occ_hub_blast_glass_a', rect: { x: 1860, y: 1210, width: 52, height: 320 }, blocksMovement: false, blocksSight: true },
    { id: 'occ_hub_blast_glass_b', rect: { x: 2670, y: 1210, width: 52, height: 320 }, blocksMovement: false, blocksSight: true },
    { id: 'occ_reagent_steam', rect: { x: 3970, y: 1380, width: 110, height: 320 }, blocksMovement: false, blocksSight: true },
    { id: 'occ_tunnel_smoke', rect: { x: 2960, y: 2420, width: 260, height: 230 }, blocksMovement: false, blocksSight: true },
  ],
  interactables: INTERACTABLES,
  workAnchors: [
    { id: 'guard_checkpoint_airlock', zoneId: 'airlock_entry', kind: 'checkpoint', position: point(980, 700), allowedRoles: ['guard', 'operator'] },
    { id: 'guard_checkpoint_security', zoneId: 'security_room', kind: 'checkpoint', position: point(720, 1240), allowedRoles: ['guard', 'operator'] },
    { id: 'guard_checkpoint_hub', zoneId: 'central_cross', kind: 'checkpoint', position: point(2290, 1400), allowedRoles: ['guard', 'operator'] },
    { id: 'scientist_sample_lab', zoneId: 'research_lab', kind: 'experiment', position: point(1850, 560), allowedRoles: ['scientist'] },
    { id: 'server_audit_station', zoneId: 'server_room', kind: 'operator_work', position: point(3000, 650), allowedRoles: ['operator', 'technician'] },
    { id: 'reagent_console_anchor', zoneId: 'reactor_room', kind: 'breakdown', position: point(3670, 1370), allowedRoles: ['technician'] },
    { id: 'medbay_triage', zoneId: 'med_bay', kind: 'medical_station', position: point(3710, 650), allowedRoles: ['medic'] },
    { id: 'engineering_repair_anchor', zoneId: 'engineering_sector', kind: 'maintenance', position: point(1020, 2060), allowedRoles: ['technician'] },
    { id: 'archive_search_anchor', zoneId: 'archive_room', kind: 'workstation', position: point(2200, 2100), allowedRoles: ['operator', 'scientist'] },
    { id: 'evac_lift_anchor', zoneId: 'evacuation_zone', kind: 'evacuation', position: point(3930, 2260), allowedRoles: ['guard', 'operator', 'medic', 'technician'] },
  ],
  navNodes: [
    { id: 'airlock_exit', zoneId: 'airlock_entry', position: point(900, 760), kind: 'evac' },
    { id: 'main_west', zoneId: 'central_cross', position: point(1160, 940), kind: 'hub' },
    { id: 'main_center', zoneId: 'central_cross', position: point(1900, 940), kind: 'hub' },
    { id: 'hub_center', zoneId: 'central_cross', position: point(2290, 1400), kind: 'hub' },
    { id: 'main_east', zoneId: 'central_cross', position: point(3180, 940), kind: 'hub' },
    { id: 'research_entry', zoneId: 'central_cross', position: point(1900, 790), kind: 'door' },
    { id: 'research_core', zoneId: 'research_lab', position: point(1900, 520), kind: 'work' },
    { id: 'server_entry', zoneId: 'central_cross', position: point(2960, 790), kind: 'door' },
    { id: 'server_core', zoneId: 'server_room', position: point(3000, 550), kind: 'work' },
    { id: 'med_core', zoneId: 'med_bay', position: point(3710, 650), kind: 'work' },
    { id: 'reagent_entry', zoneId: 'reactor_room', position: point(3440, 1490), kind: 'door' },
    { id: 'reagent_core', zoneId: 'reactor_room', position: point(3760, 1510), kind: 'incident' },
    { id: 'security_core', zoneId: 'security_room', position: point(720, 1240), kind: 'work' },
    { id: 'engineering_core', zoneId: 'engineering_sector', position: point(1120, 2060), kind: 'work' },
    { id: 'archive_core', zoneId: 'archive_room', position: point(2200, 2100), kind: 'work' },
    { id: 'tunnel_west', zoneId: 'technical_tunnels', position: point(1800, 2540), kind: 'hub' },
    { id: 'tunnel_center', zoneId: 'technical_tunnels', position: point(2500, 2540), kind: 'hub' },
    { id: 'tunnel_east', zoneId: 'technical_tunnels', position: point(3370, 2500), kind: 'door' },
    { id: 'evac_lift', zoneId: 'evacuation_zone', position: point(3930, 2260), kind: 'evac' },
  ],
  navEdges: [
    ...twoWayEdge('e_airlock_main', 'airlock_exit', 'main_west', 420, 'door_airlock_blast'),
    ...twoWayEdge('e_main_west_center', 'main_west', 'main_center', 740),
    ...twoWayEdge('e_main_center_hub', 'main_center', 'hub_center', 520),
    ...twoWayEdge('e_hub_main_east', 'hub_center', 'main_east', 820),
    ...twoWayEdge('e_main_research', 'main_center', 'research_entry', 250),
    ...twoWayEdge('e_research_door', 'research_entry', 'research_core', 300, 'door_research_quarantine', 'lab'),
    ...twoWayEdge('e_main_server', 'main_east', 'server_entry', 280),
    ...twoWayEdge('e_server_door', 'server_entry', 'server_core', 300, 'door_server_lockdown', 'server'),
    ...twoWayEdge('e_main_med', 'main_east', 'med_core', 520, 'door_medbay_slide'),
    ...twoWayEdge('e_main_reagent', 'main_east', 'reagent_entry', 620, 'door_reagent_breach', 'reactor'),
    ...twoWayEdge('e_reagent_core', 'reagent_entry', 'reagent_core', 340),
    ...twoWayEdge('e_main_security', 'main_west', 'security_core', 420, 'door_security_checkpoint'),
    ...twoWayEdge('e_security_engineering', 'security_core', 'engineering_core', 680, 'door_engineering_shutter'),
    ...twoWayEdge('e_hub_archive', 'hub_center', 'archive_core', 740, 'door_archive_lock', 'server'),
    ...twoWayEdge('e_archive_tunnel', 'archive_core', 'tunnel_center', 560),
    ...twoWayEdge('e_engineering_tunnel', 'engineering_core', 'tunnel_west', 620),
    ...twoWayEdge('e_tunnel_loop', 'tunnel_west', 'tunnel_center', 700),
    ...twoWayEdge('e_tunnel_east', 'tunnel_center', 'tunnel_east', 870),
    ...twoWayEdge('e_tunnel_evac', 'tunnel_east', 'evac_lift', 460, 'door_evac_lift', 'reactor'),
  ],
  incidentAnchors: [
    { id: 'alarm_hub', kind: 'alarm', zoneId: 'central_cross', position: point(2290, 1400) },
    { id: 'fire_server', kind: 'fire', zoneId: 'server_room', position: point(2790, 520) },
    { id: 'reactor_fault_main', kind: 'reactor_fault', zoneId: 'reactor_room', position: point(3760, 1510) },
    { id: 'server_fault_main', kind: 'server_fault', zoneId: 'server_room', position: point(3000, 650) },
    { id: 'injury_medbay', kind: 'injured_person', zoneId: 'med_bay', position: point(3710, 650) },
    { id: 'door_failure_airlock', kind: 'door_failure', zoneId: 'airlock_entry', position: point(1040, 790) },
  ],
  patrolRoutes: [
    {
      id: 'infection_outer_loop',
      nodes: [
        { x: 2290, y: 1400, zoneId: 'central_cross', pauseMs: 400 },
        { x: 1900, y: 520, zoneId: 'research_lab', pauseMs: 450 },
        { x: 3000, y: 550, zoneId: 'server_room', pauseMs: 500 },
        { x: 3760, y: 1510, zoneId: 'reactor_room', pauseMs: 520 },
        { x: 3930, y: 2260, zoneId: 'evacuation_zone', pauseMs: 420 },
        { x: 2500, y: 2540, zoneId: 'technical_tunnels', pauseMs: 480 },
        { x: 1120, y: 2060, zoneId: 'engineering_sector', pauseMs: 420 },
      ],
    },
  ],
  npcs: [
    {
      id: 'infected_alpha',
      name: 'Заражённый Альфа',
      role: 'guard',
      actorPreset: 'zombie',
      behaviorProfile: 'zombie',
      faction: 'hostile',
      spawn: point(3900, 1580),
      routeId: 'infection_outer_loop',
      homeZoneId: 'reactor_room',
      workAnchorIds: [],
      evacuationNodeId: 'airlock_exit',
      accessLevels: ['public', 'lab', 'server', 'reactor'],
      color: 0x84cc16,
      health: 132,
      maxHealth: 132,
      speed: 106,
      visionRange: 620,
      visionHalfAngleDeg: 72,
    },
    {
      id: 'infected_beta',
      name: 'Заражённый Бета',
      role: 'guard',
      actorPreset: 'zombie',
      behaviorProfile: 'zombie',
      faction: 'hostile',
      spawn: point(2540, 2560),
      routeId: 'infection_outer_loop',
      homeZoneId: 'technical_tunnels',
      workAnchorIds: [],
      evacuationNodeId: 'airlock_exit',
      accessLevels: ['public', 'lab', 'server', 'reactor'],
      color: 0x65a30d,
      health: 118,
      maxHealth: 118,
      speed: 102,
      visionRange: 590,
      visionHalfAngleDeg: 70,
    },
    {
      id: 'infected_medic',
      name: 'Заражённый медик',
      role: 'medic',
      actorPreset: 'zombie',
      behaviorProfile: 'zombie',
      faction: 'hostile',
      spawn: point(3850, 620),
      routeId: 'infection_outer_loop',
      homeZoneId: 'med_bay',
      workAnchorIds: [],
      evacuationNodeId: 'airlock_exit',
      accessLevels: ['public', 'lab', 'server', 'reactor'],
      color: 0xa3e635,
      health: 110,
      maxHealth: 110,
      speed: 112,
      visionRange: 570,
      visionHalfAngleDeg: 68,
    },
    {
      id: 'infected_warden',
      name: 'Носитель разлома',
      role: 'operator',
      actorKind: 'boss',
      actorPreset: 'zombie',
      behaviorProfile: 'zombie',
      faction: 'hostile',
      spawn: point(2290, 1400),
      routeId: 'infection_outer_loop',
      homeZoneId: 'central_cross',
      workAnchorIds: [],
      evacuationNodeId: 'airlock_exit',
      accessLevels: ['public', 'lab', 'server', 'reactor'],
      color: 0xbe123c,
      radius: 30,
      health: 260,
      maxHealth: 260,
      speed: 88,
      visionRange: 690,
      visionHalfAngleDeg: 84,
    },
  ],
  lights: LIGHTS,
  hazards: [
    { id: 'infection_spore_hub', kind: 'smoke', zoneId: 'central_cross', position: point(2290, 1400), radius: 260, intensity: 0.72, textures: [textureLayer('infection_spore_hub_texture', 'sci_fi_green_bioluminescent_ground', 0.72, 'cover', 180)] },
    { id: 'infection_spore_lab', kind: 'smoke', zoneId: 'research_lab', position: point(2110, 480), radius: 250, intensity: 1, textures: [textureLayer('infection_spore_lab_texture', 'sci_fi_green_bioluminescent_ground', 0.74, 'cover', 180)] },
    { id: 'infection_spore_reagent', kind: 'smoke', zoneId: 'reactor_room', position: point(3900, 1580), radius: 250, intensity: 0.92, textures: [textureLayer('infection_spore_reagent_texture', 'horror_grimy_glass_tile', 0.72, 'cover', 180)] },
    { id: 'infection_spore_tunnel', kind: 'smoke', zoneId: 'technical_tunnels', position: point(3000, 2540), radius: 230, intensity: 0.82, textures: [textureLayer('infection_spore_tunnel_texture', 'horror_cracked_black_floor', 0.72, 'cover', 180)] },
    { id: 'server_sparks', kind: 'sparks', zoneId: 'server_room', position: point(2790, 520), radius: 150, intensity: 0.88, textures: [textureLayer('server_sparks_texture', 'sci_fi_blue_holographic_grid', 0.72, 'cover', 160)] },
  ],
  props: [
    prop('airlock_console_a', 'airlock_console', 'airlock_entry', { x: 900, y: 620, width: 130, height: 90 }, 0x0e7490),
    prop('airlock_locker_a', 'locker', 'airlock_entry', { x: 500, y: 360, width: 92, height: 150 }, 0x475569),
    prop('airlock_crate_a', 'crate_stack', 'airlock_entry', { x: 850, y: 380, width: 130, height: 90 }, 0x78350f),
    prop('corridor_warning_a', 'warning_stripe', 'central_cross', { x: 1260, y: 890, width: 260, height: 34 }, 0xf59e0b),
    prop('corridor_warning_b', 'warning_stripe', 'central_cross', { x: 3300, y: 1060, width: 220, height: 34 }, 0xf59e0b),
    prop('hub_grate_a', 'floor_grate', 'central_cross', { x: 2030, y: 1260, width: 220, height: 110 }, 0x334155),
    prop('hub_terminal_a', 'wall_terminal', 'central_cross', { x: 2440, y: 1230, width: 120, height: 86 }, 0x0891b2),
    prop('research_bench_a', 'lab_bench', 'research_lab', { x: 1600, y: 500, width: 260, height: 92 }, 0x334155),
    prop('research_bench_b', 'lab_bench', 'research_lab', { x: 1900, y: 620, width: 260, height: 92 }, 0x334155),
    prop('research_microscope_a', 'microscope', 'research_lab', { x: 1730, y: 470, width: 70, height: 62 }, 0xe2e8f0),
    prop('research_sample_tank_a', 'sample_tank', 'research_lab', { x: 2130, y: 340, width: 90, height: 180 }, 0x14b8a6),
    prop('server_rack_a', 'server_rack', 'server_room', { x: 2740, y: 350, width: 92, height: 290 }, 0x0f172a),
    prop('server_rack_b', 'server_rack', 'server_room', { x: 2870, y: 350, width: 92, height: 290 }, 0x0f172a),
    prop('server_console_a', 'server_console', 'server_room', { x: 3060, y: 560, width: 170, height: 88 }, 0x1d4ed8),
    prop('med_bed_a', 'medical_bed', 'med_bay', { x: 3490, y: 560, width: 180, height: 84 }, 0xf8fafc),
    prop('med_bed_b', 'medical_bed', 'med_bay', { x: 3810, y: 560, width: 180, height: 84 }, 0xf8fafc),
    prop('med_monitor_a', 'med_monitor', 'med_bay', { x: 3690, y: 690, width: 96, height: 78 }, 0x16a34a),
    prop('reagent_core_a', 'reactor_core', 'reactor_room', { x: 3680, y: 1390, width: 220, height: 220 }, 0xdc2626),
    prop('reagent_pipe_a', 'pipe_run', 'reactor_room', { x: 4040, y: 1260, width: 70, height: 440 }, 0x7f1d1d),
    prop('security_console_a', 'wall_terminal', 'security_room', { x: 500, y: 1160, width: 190, height: 90 }, 0x1d4ed8),
    prop('security_locker_a', 'locker', 'security_room', { x: 900, y: 1160, width: 90, height: 150 }, 0x475569),
    prop('engineering_turbine_a', 'turbine', 'engineering_sector', { x: 820, y: 1950, width: 230, height: 170 }, 0x92400e),
    prop('engineering_pipe_a', 'pipe_run', 'engineering_sector', { x: 700, y: 2190, width: 760, height: 70 }, 0x7f1d1d),
    prop('archive_terminal_a', 'wall_terminal', 'archive_room', { x: 1880, y: 2050, width: 150, height: 90 }, 0x312e81),
    prop('archive_crate_a', 'crate_stack', 'archive_room', { x: 2380, y: 2080, width: 150, height: 110 }, 0x78350f),
    prop('tunnel_grate_a', 'floor_grate', 'technical_tunnels', { x: 1950, y: 2480, width: 240, height: 110 }, 0x334155),
    prop('tunnel_pipe_a', 'pipe_run', 'technical_tunnels', { x: 2700, y: 2630, width: 520, height: 58 }, 0x7f1d1d),
    prop('evac_warning_a', 'warning_stripe', 'evacuation_zone', { x: 3720, y: 2050, width: 360, height: 38 }, 0xfacc15),
    prop('evac_terminal_a', 'wall_terminal', 'evacuation_zone', { x: 4100, y: 2210, width: 120, height: 88 }, 0x854d0e),
  ],
  customObjects: CUSTOM_OBJECTS,
  triggers: [
    {
      id: 'trigger_central_breach',
      label: 'Вход в безопасную зону',
      zoneId: 'central_cross',
      rect: { x: 2050, y: 1190, width: 480, height: 430 },
      event: 'onEnter',
      repeatable: false,
      cooldownMs: 0,
      textures: [textureLayer('trigger_central_breach_texture', 'sci_fi_green_bioluminescent_ground', 0.45, 'cover', 200)],
      actions: [
        { kind: 'set_alarm', enabled: true },
        { kind: 'set_light_flicker', lightIds: ['light_main_corridor', 'light_research_red'], enabled: true, durationMs: 5000 },
        { kind: 'play_sound', soundSourceIds: ['sound_breach_alarm'] },
        { kind: 'spawn_smoke', position: point(2290, 1400), radius: 300 },
      ],
    },
    {
      id: 'trigger_reagent_spores',
      label: 'Опасная зона реагентов',
      zoneId: 'reactor_room',
      rect: { x: 3760, y: 1440, width: 300, height: 270 },
      event: 'onEnter',
      repeatable: true,
      cooldownMs: 12000,
      textures: [textureLayer('trigger_reagent_spores_texture', 'horror_grimy_glass_tile', 0.46, 'cover', 190)],
      actions: [
        { kind: 'set_light_flicker', lightIds: ['light_reagent_red'], enabled: true, durationMs: 4200 },
        { kind: 'spawn_smoke', position: point(3900, 1580), radius: 250 },
      ],
    },
    {
      id: 'trigger_tunnel_risk_path',
      label: 'Рискованный путь тоннелей',
      zoneId: 'technical_tunnels',
      rect: { x: 2360, y: 2420, width: 760, height: 260 },
      event: 'onEnter',
      repeatable: true,
      cooldownMs: 15000,
      textures: [textureLayer('trigger_tunnel_risk_path_texture', 'horror_cracked_black_floor', 0.42, 'cover', 190)],
      actions: [
        { kind: 'set_light_flicker', lightIds: ['light_tunnels'], enabled: true, durationMs: 3600 },
        { kind: 'spawn_smoke', position: point(3000, 2540), radius: 220 },
      ],
    },
  ],
  soundDefinitions: [
    { id: 'footstep_soft', label: 'Шаги по лабораторному полу', kind: 'footstep' },
    { id: 'impact_metal', label: 'Металлический удар', kind: 'impact' },
    { id: 'alarm_siren', label: 'Сирена тревоги', kind: 'alarm' },
    ...MYSTERY_ROOM_STANDARD_AMBIENT_SOUND_DEFINITIONS.map((definition) => ({ ...definition })),
  ],
  soundSources: [
    { id: 'ambient_airlock', label: 'Фон приёмного шлюза', zoneId: 'airlock_entry', position: point(720, 620), soundId: 'ambient_track_2', radius: 720, volume: 0.26, loop: true },
    { id: 'ambient_main_corridor', label: 'Фон главного коридора', zoneId: 'central_cross', position: point(1900, 940), soundId: 'ambient_track_1', radius: 1050, volume: 0.32, loop: true },
    { id: 'ambient_hub', label: 'Фон безопасной зоны', zoneId: 'central_cross', position: point(2290, 1400), soundId: 'ambient_track_5', radius: 880, volume: 0.28, loop: true },
    { id: 'ambient_research_lab', label: 'Фон лаборатории образцов', zoneId: 'research_lab', position: point(1900, 520), soundId: 'ambient_track_3', radius: 720, volume: 0.28, loop: true },
    { id: 'ambient_server_room', label: 'Фон серверной', zoneId: 'server_room', position: point(3000, 550), soundId: 'ambient_track_4', radius: 760, volume: 0.3, loop: true },
    { id: 'ambient_reagent_breach', label: 'Фон склада реагентов', zoneId: 'reactor_room', position: point(3760, 1510), soundId: 'ambient_track_6', radius: 920, volume: 0.34, loop: true },
    { id: 'ambient_tunnels', label: 'Фон технических тоннелей', zoneId: 'technical_tunnels', position: point(2500, 2540), soundId: 'ambient_track_8', radius: 920, volume: 0.3, loop: true },
    { id: 'sound_breach_alarm', label: 'Сирена разлома', zoneId: 'central_cross', position: point(2290, 1400), soundId: 'alarm_siren', radius: 980, volume: 0.9, loop: false },
  ],
};

export const LISAD_9_INFECTION_MAP: MysteryRoomMapDefinition = expandMapToEditorBounds(LISAD_9_INFECTION_BASE_MAP);
